import {
  Box,
  Spinner
} from "@chakra-ui/react";
import React, {
  useEffect,
  useMemo
} from "react";
import { useHistory } from "react-router-dom";
import {
  BsChatRightQuote,
  BsFillInboxesFill
} from "react-icons/bs";
import { FaHistory } from "react-icons/fa";
import {
  format,
  isToday,
  isPast,
  isThisWeek
} from "date-fns";

import { SolidButton } from "src/components/button";
import { Header } from "src/components/shared/Header";
import { Text } from "src/components/text";
import { theme } from "src/components/theme";
import { missionDayLabel } from "src/utils/utcDate";
import { useAuthContext } from "src/hooks/useAuthContext";
import { useConversationsApi } from "src/api";

import { QuickActionBox } from "./components/QuickActionBox";

export const HomePage: React.FC = () => {
  const history = useHistory();
  const { authDataState } = useAuthContext();
  const [ conversationsRes, conversationsReq ] = useConversationsApi("GET_CONVERSATIONS");

  useEffect(() => {
    // Get the soonest upcoming conversation
    conversationsReq({
      skip: 0,
      filter: { endTime: { $gt: new Date() } },
      sort: { "fullConversation.startTime": 1 },
      limit: 1
    });
  }, [ conversationsReq ]);

  const upcomingSession = useMemo(() => conversationsRes.data?.results?.[ 0 ], [ conversationsRes ]);

  // Nicely format the upcoming session partner
  const participantInSession = useMemo(() => {
    const creator = upcomingSession?.participants?.find(p => p.creator);
    const partner = upcomingSession?.participants?.find(p => !p.creator);

    if (creator?.user._id === authDataState.user?._id) {
      return partner;
    } else {
      return creator;
    }
  }, [ upcomingSession, authDataState ]);

  // Nicely format the upcoming session time / date
  const upcomingSessionTime = useMemo(() => {
    if (upcomingSession?.startTime) {
      const startTime = new Date(upcomingSession.startTime);

      if (isPast(startTime)) {
        return `until ${format(new Date(upcomingSession.endTime), "HH:mm")}`;
      } else if (isToday(startTime)) {
        return `today at ${format(new Date(upcomingSession.startTime), "HH:mm")}`;
      } else if (isThisWeek(startTime)) {
        return `on ${format(new Date(upcomingSession.startTime), "EEEE 'at' HH:mm")}`;
      } else {
        return `on ${format(new Date(upcomingSession.startTime), "EEEE do MMMM")}`;
      }
    }
  }, [ upcomingSession ]);

  return (
    <Box p={10}>
      <Header />

      {!authDataState.user ? <Spinner /> : (

        <Box
          paddingY={25}
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
        >
          <Text
            as="h2"
            bold
          >
            {`Hi, ${authDataState.user?.name || ""}`}
          </Text>

          {(authDataState.user?.mission && authDataState.user.mission.name !== "GENERAL") && (
            <>
              <Text as="p">
                {`Your mission is: ${authDataState.user.mission.name}`}
              </Text>

              <Text as="p">
                {missionDayLabel(authDataState.user.mission.startDate)}
              </Text>
            </>
          )}

          <Box
            paddingY={25}
            marginY={25}
            border={`2px solid ${theme.colors.main}`}
            borderRadius={20}
            width="80%"
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
          >
            {conversationsRes.pending ? <Spinner /> : upcomingSession ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                flexDirection="column"
              >
                <Text as="p">
                  {`Your ${isPast(new Date(upcomingSession.startTime)) ? "current" : "next"} session is with ${participantInSession?.user.name} ${upcomingSessionTime}`}
                </Text>

                <SolidButton
                  marginTop={5}
                  onClick={() => history.push(`/conversation/${upcomingSession._id}`)}
                >
                  View session
                </SolidButton>

              </Box>
            ) : (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                flexDirection="column"
              >
                <Text as="p">
                  You have no upcoming sessions.
                </Text>

                <SolidButton
                  marginTop={5}
                  onClick={() => history.push("/conversation/create")}
                >
                  Create a session
                </SolidButton>

              </Box>
            )}

          </Box>

          <Box
            display="flex"
            padding={5}
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
          >
            <Text
              as="p"
              bold
            >
              Quick Actions
            </Text>
          </Box>

          <Box
            marginY={25}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexDirection="row"
          >
            <QuickActionBox
              Icon={BsChatRightQuote}
              text="New session"
              handleClick={() => history.push("/conversation/create")}
            />

            <QuickActionBox
              Icon={BsFillInboxesFill}
              text="Manage invites"
              handleClick={() => history.push("/conversation/invitations")}
            />

            <QuickActionBox
              Icon={FaHistory}
              text="Session history"
              handleClick={() => history.push("/conversation/history")}
            />

          </Box>
        </Box>
      )}

    </Box>
  );
};
